import AgGridLayout from "@wrappers/layouts/AgGridLayout";
import { showToast } from "@ts/modals";
import { deleteReports, updateReportsOrder } from "@ts/retrieve-data";
import {
    ColDef,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    RowNode,
} from "ag-grid-community";
import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { Button, Popconfirm, Tabs, TabsProps } from "antd";
import {
    IReportForm,
    IReportsData,
    IUserRecord,
    TAgGridRowData,
    TSelectedReportRowDataForAccessManagement,
} from "@ts/common-types";
import { FaTrashAlt } from "react-icons/fa";
import ReportTypeCellRenderer from "@/components/cell-renderers/ReportTypeCellRenderer";
import usePermissionCheck from "@/hooks/usePermissionCheck";
import { REPORTS_TYPES, REPORTS_TYPES_IDS } from "@/ts/common-types";
import { partition } from "underscore";
import ManageReportAccessModal from "./ManageReportAccessModal";
import { titleCase } from "@/ts/utils";
import { useSelector } from "react-redux";

const RevstackReportsGrid: React.FC<IReportForm> = ({
    existingReportsData,
    setSelectedModule,
    setGetUpdatedReports,
}) => {
    const [popoverLoading, setPopoverLoading] = useState<boolean>(false);
    const [selectedReports, setSelectedReports] = useState<Array<string>>([]);
    // eslint-disable-next-line
    const [gridApi, setGridApi] = useState<GridApi>();
    const onSelectionChanged = (event: GridReadyEvent) => {
        const selectedNodes = event.api.getSelectedNodes();
        const reports = selectedNodes.map(
            (node: RowNode) => node.data.report_id
        );
        setSelectedReports(reports);
    };
    const [activeTab, setActiveTab] = useState<string>(REPORTS_TYPES.THREE_P_REPORT);
    const [fixedRowData, setFixedRowData] = useState<Array<IReportsData>>([]);
    const [rowData, setRowData] = useState<Array<IReportsData>>([]);
    const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
    const [saveLoader, setSaveLoader] = useState<boolean>(false);
    const [openAccessManageModal, setOpenAccessManageModal] =
        useState<boolean>(false);
    const [selectedReportData, setSelectedReportData] =
        useState<TSelectedReportRowDataForAccessManagement | null>(null);
    const userData = useSelector(
        (state: { userData: { value: IUserRecord } }) => state.userData.value
    );

    const { canUpdateReport, canDeleteReport } = usePermissionCheck();

    const setRowDataHandler = (
        reportType: REPORTS_TYPES,
        reportsData: Array<IReportsData>
    ) => {
        // Filter reports based on report type
        const reports = reportsData?.filter((report) =>
            reportType === REPORTS_TYPES.THREE_P_REPORT
                ? report.report_type_id === REPORTS_TYPES_IDS.THREE_P_REPORT
                : report.report_type_id === REPORTS_TYPES_IDS.COMMON
        );

        // Separate reports with and without report_order_id
        const [reportsWithOrder, reportsWithoutOrder] = partition(
            reports,
            (report) => report.report_order_id !== null
        );

        // Sort reports that have a report_order_id
        reportsWithOrder.sort((a, b) => a.report_order_id - b.report_order_id);

        // Assign new order IDs to reports without report_order_id
        let nextOrderId =
            reportsWithOrder.length > 0
                ? (reportsWithOrder[reportsWithOrder.length - 1]
                    .report_order_id ?? 0) + 1
                : 1;
        const updatedReportsWithoutOrder = reportsWithoutOrder.map(
            (report) => ({
                ...report,
                report_order_id: nextOrderId++,
            })
        );

        // Merge both lists
        const updatedRowData = [
            ...reportsWithOrder,
            ...updatedReportsWithoutOrder,
        ];

        setRowData(updatedRowData);
        return updatedRowData;
    };

    useEffect(() => {
        const reports = setRowDataHandler(
            REPORTS_TYPES.THREE_P_REPORT,
            existingReportsData
        );
        setFixedRowData(reports);
        setActiveTab(REPORTS_TYPES.THREE_P_REPORT);
    }, [existingReportsData]);

    const onDeleteClickHandler = async () => {
        try {
            setPopoverLoading(true);
            const reportsType =
                activeTab === REPORTS_TYPES.THREE_P_REPORT
                    ? REPORTS_TYPES_IDS.THREE_P_REPORT
                    : REPORTS_TYPES_IDS.COMMON;
            const response = await deleteReports(
                selectedReports,
                reportsType,
                existingReportsData
            );
            if (!response.status) {
                throw new Error();
            }
            showToast("success", response.message);
            setPopoverLoading(false);
            setGetUpdatedReports((prev) => !prev);
            setSelectedModule("");
        } catch (err) {
            console.error(err);
            showToast("danger", (err as Error).message);
            Sentry.captureException(err);
            setPopoverLoading(false);
        }
    };

    const onUpdateReportsSubmit = async () => {
        try {
            setSaveLoader(true);

            const updateReportOrderData = rowData.map((report) => {
                return {
                    report_id: report.report_id,
                    report_order_id: report.report_order_id,
                };
            });

            const response = await updateReportsOrder({
                reports: updateReportOrderData,
            });
            setGetUpdatedReports((prev) => !prev);
            if (response.status) {
                showToast("success", response.message);
            } else {
                showToast("danger", response.message);
            }
            setSaveLoader(false);
            setIsSaveDisabled(true);
        } catch (err) {
            showToast("danger", (err as Error).message);
            Sentry.captureException(err);
            setSaveLoader(false);
        }
    };

    const handleManageAccessClick = (
        params: TSelectedReportRowDataForAccessManagement
    ) => {
        setSelectedReportData(params);
        setOpenAccessManageModal(true);
    };

    const [columnDefs] = useState<ColDef[]>([
        {
            field: "report_name",
            headerName: "Report Name",
            headerCheckboxSelection: true,
            checkboxSelection: canUpdateReport,
            cellStyle: {
                margin: "0",
            },
            rowDrag: true,
        },
        {
            field: "report_id",
            headerName: "Report Id",
            hide: true,
        },
        {
            field: "embed_url",
            headerName: "Report Url",
            hide: true,
        },
        {
            field: "report_type_id",
            headerName: "Report Type",
            hide: false,
            cellRenderer: ReportTypeCellRenderer,
            cellRendererParams: {
                setGetUpdatedReports,
                existingReportsData,
            },
            minWidth: 120,
        },
        {
            field: "access_type",
            headerName: "Access Type",
            minWidth: 100,
            valueFormatter: (params) => {
                const value = params.value;
                return titleCase(value);
            },
        },
        {
            field: "report_order_id",
            headerName: "Report Order",
            hide: true,
        },
        {
            headerName: "Actions",
            field: "manage_access",
            cellRenderer: (params: ICellRendererParams) => {
                return (
                    <Button
                        className="ag-btn manage-access-btn"
                        onClick={() => handleManageAccessClick(params.data)}
                    >
                        Manage Access
                    </Button>
                );
            },
            minWidth: 100,
        },
    ]);

    const onRowDragEnd = (event: any) => {
        if (!gridApi) return;

        // Extract and update report_order_id
        const rowDataWithReportOrder = gridApi
            .getRenderedNodes()
            .map((node, index) => ({
                ...node.data,
                report_order_id: index + 1,
            }));

        setRowData(rowDataWithReportOrder);

        // Check if report_order_id matches for each report_id
        const isSameOrder = fixedRowData.every((fixedReport) => {
            const updatedReport = rowDataWithReportOrder.find(
                (report) => report.report_id === fixedReport.report_id
            );
            return (
                updatedReport?.report_order_id === fixedReport.report_order_id
            );
        });

        setIsSaveDisabled(isSameOrder);
    };

    const tabItems: TabsProps["items"] = [
        {
            key: REPORTS_TYPES.THREE_P_REPORT,
            label: "3P Report",
        },
        {
            key: REPORTS_TYPES.COMMON,
            label: "Common",
        },
    ];

    const onReportTypeChange = (reportType: REPORTS_TYPES) => {
        setRowDataHandler(reportType, existingReportsData);
        setActiveTab(reportType);
    };

    return (
        <>
            <div>
                <Tabs
                    activeKey={activeTab}
                    items={tabItems}
                    className=""
                    onChange={(key) => {
                        onReportTypeChange(key as REPORTS_TYPES);
                    }}
                />

                <Popconfirm
                    title="Alert"
                    description="Confirm to delete selected reports?"
                    onConfirm={onDeleteClickHandler}
                    placement="leftBottom"
                    okButtonProps={{ loading: popoverLoading }}
                    disabled={!canDeleteReport}
                >
                    <Button
                        danger
                        disabled={
                            selectedReports.length === 0 || !canDeleteReport
                        }
                        style={{
                            position: "relative",
                            top: "0",
                            left: "75%",
                        }}
                    >
                        Delete &nbsp;{" "}
                        <FaTrashAlt
                            style={{
                                fontSize: "12px",
                                transform: "translateY(-10%)",
                            }}
                        />
                    </Button>
                </Popconfirm>
                <div
                    className="ag-theme-alpine border"
                    style={{
                        marginTop: "1rem",
                        height: "60vh",
                        width: "100%",
                    }}
                >
                    <AgGridLayout
                        rowData={rowData as TAgGridRowData}
                        columnDefs={columnDefs}
                        setGridApi={setGridApi}
                        isTotalRowsReq={false}
                        onSelectionChanged={onSelectionChanged}
                        rowDragManaged={true}
                        onRowDragEnd={onRowDragEnd}
                    />
                    <Button
                        type="primary"
                        className="mt-2"
                        disabled={isSaveDisabled}
                        loading={saveLoader}
                        onClick={() => onUpdateReportsSubmit()}
                    >
                        {saveLoader ? "Saving..." : "Save"}
                    </Button>
                </div>
            </div>
            <ManageReportAccessModal
                open={openAccessManageModal}
                setOpen={setOpenAccessManageModal}
                title={`Manage access for ${selectedReportData ? selectedReportData.report_name : ""
                    } report`}
                selectedReportData={selectedReportData}
                setGetUpdatedReports={setGetUpdatedReports}
                userData={userData}
            />
        </>
    );
};

export default RevstackReportsGrid;
